import React, { useRef, useState } from "react";
import { Camera, Upload, Plus, Trash2, ScanLine, Sparkles, AlertTriangle, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { resizeImage, money, moneyPlain, CURRENCIES, currencyInfo } from "@/lib/format";

function MoneyInput({ value, onChange, currency = "IDR", testId, className = "" }) {
  const dec = currencyInfo(currency).decimals;
  const [draft, setDraft] = useState(null);
  const shown = draft !== null ? draft : value ? moneyPlain(value, currency) : "";
  const sanitize = (raw) => {
    if (dec > 0) {
      raw = raw.replace(/,/g, ".").replace(/[^\d.]/g, "");
      const parts = raw.split(".");
      if (parts.length > 2) {
        const d = parts.pop();
        raw = parts.join("") + "." + d; // last dot = decimal, earlier = grouping
      }
      const p2 = raw.split(".");
      if (p2[1]?.length > dec) raw = p2[0] + "." + p2[1].slice(0, dec);
    } else {
      raw = raw.replace(/[^\d]/g, "");
    }
    return raw;
  };
  return (
    <input
      data-testid={testId}
      inputMode={dec > 0 ? "decimal" : "numeric"}
      value={shown}
      onFocus={() => setDraft(value ? String(value) : "")}
      onChange={(e) => {
        const s = sanitize(e.target.value);
        setDraft(s);
        onChange(s === "" ? 0 : parseFloat(s) || 0);
      }}
      onBlur={() => setDraft(null)}
      placeholder="0"
      className={`h-11 rounded-lg border border-[var(--gz-line)] bg-transparent px-3 text-right text-sm outline-none focus:border-[var(--gz-primary)] transition-colors ${className}`}
    />
  );
}

function NegMoneyInput({ value, onChange, currency = "IDR", testId, className = "" }) {
  const dec = currencyInfo(currency).decimals;
  const [draft, setDraft] = useState(null);
  const shown = draft !== null ? draft : value ? moneyPlain(value, currency) : "";
  return (
    <input
      data-testid={testId}
      inputMode={dec > 0 ? "decimal" : "numeric"}
      value={shown}
      onFocus={() => setDraft(value ? String(value) : "")}
      onChange={(e) => {
        const sign = e.target.value.trim().startsWith("-") ? "-" : "";
        let raw = e.target.value.replace(/,/g, ".").replace(dec > 0 ? /[^\d.]/g : /[^\d]/g, "");
        if (dec > 0) {
          const parts = raw.split(".");
          if (parts.length > 2) {
            const d = parts.pop();
            raw = parts.join("") + "." + d;
          }
        }
        setDraft(sign + raw);
        onChange(raw === "" ? 0 : (sign ? -1 : 1) * (parseFloat(raw) || 0));
      }}
      onBlur={() => setDraft(null)}
      placeholder="0"
      className={`h-11 rounded-lg border border-[var(--gz-line)] bg-transparent px-3 text-right text-sm outline-none focus:border-[var(--gz-primary)] transition-colors ${className}`}
    />
  );
}

function ChargeRow({ label, pct, onPct, amount, onAmount, currency, pctTestId, amtTestId }) {
  return (
    <div>
      <span className="block text-xs text-[var(--gz-muted)] mb-1">{label}</span>
      <div className="flex items-center gap-2">
        <div className="relative w-20 shrink-0">
          <input
            data-testid={pctTestId}
            inputMode="decimal"
            value={pct || ""}
            onChange={(e) => onPct(parseFloat(e.target.value.replace(/[^\d.]/g, "")) || 0)}
            placeholder="0"
            className="w-full h-11 rounded-lg border border-[var(--gz-line)] bg-transparent pl-3 pr-6 text-right outline-none focus:border-[var(--gz-primary)] transition-colors"
            aria-label={`${label} percent`}
          />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-[var(--gz-muted)]">%</span>
        </div>
        <MoneyInput testId={amtTestId} value={amount || 0} onChange={onAmount} currency={currency} className="flex-1 min-w-0" />
      </div>
    </div>
  );
}

export default function ReceiptSection({ items, charges, currency = "IDR", onUpload, scanning, onItemChange, onAddItem, onRemoveItem, onCharges, summary, onContinue }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const m = (v) => money(v, currency);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image of the receipt.");
      return;
    }
    setPreview(URL.createObjectURL(file));
    try {
      const resized = await resizeImage(file);
      await onUpload(resized);
    } catch (err) {
      toast.error("Could not read that image. Try another photo.");
    }
  };

  const diff = summary?.reconciliation_diff;
  const grand = summary?.grand_total ?? 0;

  return (
    <div data-testid="receipt-section" className="gz-rise">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="text-[15px] font-semibold">Scan the receipt</h2>
        <label className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-[var(--gz-muted)]">Currency</span>
          <select
            data-testid="currency-select"
            value={currency}
            onChange={(e) => onCharges({ currency: e.target.value })}
            className="h-9 rounded-lg border border-[var(--gz-line)] bg-[var(--gz-card)] px-2 text-sm font-medium outline-none focus:border-[var(--gz-primary)] transition-colors"
          >
            {Object.values(CURRENCIES).map((c) => (
              <option key={c.code} value={c.code}>
                {c.flag} {c.code}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <button
          data-testid="scan-camera-btn"
          onClick={() => fileRef.current?.setAttribute("capture", "environment") || fileRef.current?.click()}
          disabled={scanning}
          className="h-24 rounded-xl border border-[var(--gz-line)] flex flex-col items-center justify-center gap-1.5 active:scale-95 transition-transform disabled:opacity-50"
        >
          <Camera size={22} className="text-[var(--gz-primary)]" />
          <span className="text-sm font-medium">Take photo</span>
        </button>
        <button
          data-testid="scan-upload-btn"
          onClick={() => {
            fileRef.current?.removeAttribute("capture");
            fileRef.current?.click();
          }}
          disabled={scanning}
          className="h-24 rounded-xl border border-[var(--gz-line)] flex flex-col items-center justify-center gap-1.5 active:scale-95 transition-transform disabled:opacity-50"
        >
          <Upload size={22} className="text-[var(--gz-primary)]" />
          <span className="text-sm font-medium">Upload</span>
        </button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="receipt-file-input" />

      {items.length === 0 && (
        <button
          data-testid="add-item-manual-btn"
          onClick={onAddItem}
          disabled={scanning}
          className="w-full mb-4 h-11 rounded-xl border border-[var(--gz-line)] text-sm font-medium flex items-center justify-center gap-2 active:scale-[0.99] transition disabled:opacity-50"
        >
          <Plus size={16} /> Add item manually
        </button>
      )}

      {scanning && (
        <div className="relative overflow-hidden rounded-xl border border-[var(--gz-line)] bg-[var(--gz-primary)]/5 p-6 mb-4 text-center">
          {preview && <img src={preview} alt="receipt" className="absolute inset-0 w-full h-full object-cover opacity-15" />}
          <div className="gz-scan-line" />
          <div className="relative flex flex-col items-center gap-2">
            <Sparkles size={22} className="text-[var(--gz-primary)] animate-pulse" />
            <p className="text-sm font-medium">Reading your receipt…</p>
            <p className="text-xs text-[var(--gz-muted)]">gigazip reads the items and detects the currency</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-[var(--gz-muted)]">Line items</h3>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 text-[var(--gz-muted)] text-sm border border-dashed border-[var(--gz-line)] rounded-xl mb-4">
          <ScanLine size={22} className="mx-auto mb-2 opacity-60" />
          Scan a receipt or add items manually.
        </div>
      ) : (
        <div className="space-y-2 mb-4">
          {items.map((it) => (
            <div key={it.id} data-testid={`item-row-${it.id}`} className="rounded-xl border border-[var(--gz-line)] p-3 gz-rise">
              <div className="flex items-center gap-2">
                <input
                  data-testid={`item-name-${it.id}`}
                  value={it.name}
                  onChange={(e) => onItemChange(it.id, { name: e.target.value })}
                  placeholder="Item name"
                  className="flex-1 min-w-0 h-10 bg-transparent outline-none text-sm font-medium border-b border-transparent focus:border-[var(--gz-primary)] transition-colors"
                />
                <button
                  data-testid={`remove-item-${it.id}`}
                  onClick={() => onRemoveItem(it.id)}
                  aria-label="Remove item"
                  className="grid place-items-center h-10 w-10 shrink-0 rounded-full text-[var(--gz-muted)] hover:text-red-500 active:scale-90 transition-transform"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <div className="flex items-center gap-1 shrink-0">
                  <span className="text-xs text-[var(--gz-muted)]">Qty</span>
                  <input
                    data-testid={`item-qty-${it.id}`}
                    inputMode="numeric"
                    value={it.qty}
                    onChange={(e) => {
                      const q = e.target.value.replace(/[^\d.]/g, "");
                      onItemChange(it.id, { qty: q === "" ? 0 : parseFloat(q) });
                    }}
                    className="w-12 h-10 rounded-lg border border-[var(--gz-line)] bg-transparent px-2 text-center text-sm outline-none focus:border-[var(--gz-primary)] transition-colors"
                  />
                </div>
                <span className="text-[var(--gz-muted)] shrink-0">×</span>
                <MoneyInput
                  testId={`item-price-${it.id}`}
                  value={it.unit_price}
                  onChange={(v) => onItemChange(it.id, { unit_price: v })}
                  currency={currency}
                  className="flex-1 min-w-0"
                />
                <span data-testid={`item-total-${it.id}`} className="shrink-0 whitespace-nowrap text-right font-semibold text-xs tabular-nums pl-1">{m(it.qty * it.unit_price)}</span>
              </div>
            </div>
          ))}
          <button
            data-testid="add-item-manual-btn"
            onClick={onAddItem}
            disabled={scanning}
            className="w-full h-11 rounded-xl border border-dashed border-[var(--gz-line)] text-sm font-medium text-[var(--gz-muted)] flex items-center justify-center gap-2 active:scale-[0.99] transition disabled:opacity-50"
          >
            <Plus size={16} /> Add item manually
          </button>
        </div>
      )}

      {/* Charges (only once there are items to charge) */}
      {items.length > 0 && (
      <div className="rounded-xl border border-[var(--gz-line)] p-4 space-y-3 mb-4">
        <h3 className="text-sm font-semibold text-[var(--gz-muted)]">Charges</h3>
        <p className="text-xs text-[var(--gz-muted)]">Enter the exact amounts from the receipt. Use the % box to estimate an amount.</p>

        <ChargeRow
          label="Service charge"
          pct={charges.service_pct}
          onPct={(v) => onCharges({ service_pct: v, service_amount: null })}
          amount={charges.service_amount != null ? charges.service_amount : summary?.service_amount || 0}
          onAmount={(v) => onCharges({ service_amount: v })}
          currency={currency}
          pctTestId="service-pct-input"
          amtTestId="service-amount-input"
        />
        <ChargeRow
          label="Tax"
          pct={charges.tax_pct}
          onPct={(v) => onCharges({ tax_pct: v, tax_amount: null })}
          amount={charges.tax_amount != null ? charges.tax_amount : summary?.tax_amount || 0}
          onAmount={(v) => onCharges({ tax_amount: v })}
          currency={currency}
          pctTestId="tax-pct-input"
          amtTestId="tax-amount-input"
        />
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm">
            <span className="block text-xs text-[var(--gz-muted)] mb-1">Discount</span>
            <MoneyInput testId="discount-input" value={charges.discount_amount} onChange={(v) => onCharges({ discount_amount: v })} currency={currency} className="w-full" />
          </label>
          <label className="text-sm">
            <span className="block text-xs text-[var(--gz-muted)] mb-1">Rounding</span>
            <NegMoneyInput testId="rounding-input" value={charges.rounding_adjustment || 0} onChange={(v) => onCharges({ rounding_adjustment: v })} currency={currency} className="w-full" />
          </label>
        </div>
        <label className="text-sm block">
          <span className="block text-xs text-[var(--gz-muted)] mb-1">Receipt total</span>
          <MoneyInput testId="printed-total-input" value={charges.printed_total || 0} onChange={(v) => onCharges({ printed_total: v || null })} currency={currency} className="w-full" />
        </label>
      </div>
      )}

      {/* Reconciliation: computed grand total vs the receipt total */}
      {items.length > 0 && charges.printed_total ? (
        diff === 0 ? (
          <div data-testid="reconcile-ok" className="flex items-center gap-2 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-4 py-3 text-sm">
            <CheckCircle2 size={18} /> Your total matches the receipt ({m(charges.printed_total)}).
          </div>
        ) : (
          <div data-testid="reconcile-warn" className="flex items-start gap-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 px-4 py-3 text-sm">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <div>
              Computed total <b>{m(grand)}</b> doesn't match the receipt total <b>{m(charges.printed_total)}</b>, off by{" "}
              <b>{m(Math.abs(diff))}</b>. Check items, service, tax, discount or the rounding line.
            </div>
          </div>
        )
      ) : null}

      {items.length > 0 && (
        <button
          data-testid="continue-to-assign-btn"
          onClick={onContinue}
          className="mt-5 w-full py-3.5 rounded-full bg-[var(--gz-primary)] text-white font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
        >
          Continue to people <ArrowRight size={18} />
        </button>
      )}
    </div>
  );
}
